import { BOOTSTRAP_QUERY_STALE_TIME, queryClient } from '@/utils/reactQueryClient';
import { createNotificationController } from '@saas/api-client';
import { globalAuthRequestFunction } from '../http/request';
import { makeMutation, makeQuery } from '../http/queryUtils';

const notificationController = createNotificationController(globalAuthRequestFunction);

// --- Keys factory ---

const notificationServiceKeys = {
	base: 'NOTIFICATION',
	list: (params: Parameters<typeof notificationController.getNotifications>[0]) =>
		[notificationServiceKeys.base, 'list', params] as const,
	// Params (the companyId) are part of the key: the count is workspace-scoped, and a shared key
	// would let a prefetch for the cookie's workspace serve another workspace's badge from cache.
	unreadCount: (params: Parameters<typeof notificationController.getUnreadCount>[0]) =>
		[notificationServiceKeys.base, 'unread-count', params] as const,
	preferences: () => [notificationServiceKeys.base, 'preferences'] as const,
};

// --- Invalidation map ---

const invalidateNotificationQueries = {
	all: () => queryClient.invalidateQueries({ queryKey: [notificationServiceKeys.base] }),
	list: () => queryClient.invalidateQueries({ queryKey: [notificationServiceKeys.base, 'list'] }),
	unreadCount: () => queryClient.invalidateQueries({ queryKey: [notificationServiceKeys.base, 'unread-count'] }),
	preferences: () => queryClient.invalidateQueries({ queryKey: notificationServiceKeys.preferences() }),
};

// --- Queries ---

const useGetNotifications = makeQuery({
	queryFunction: notificationController.getNotifications,
	queryKeyFactory: (params) => [...notificationServiceKeys.list(params)],
});

// The topbar bell reads the unread count on every authenticated boot, so it joins the t=0
// bootstrap prefetch and pins the bootstrap staleness. Reads and the SSE stream invalidate it,
// which ignores staleness, so the badge still updates live.
const useGetUnreadCount = makeQuery({
	queryFunction: notificationController.getUnreadCount,
	queryKeyFactory: (params) => [...notificationServiceKeys.unreadCount(params)],
	defaultOptions: { staleTime: BOOTSTRAP_QUERY_STALE_TIME },
});

const useGetPreferences = makeQuery({
	queryFunction: notificationController.getPreferences,
	queryKeyFactory: () => [...notificationServiceKeys.preferences()],
});

// --- Mutations ---

const useMarkAsRead = makeMutation({
	mutationFunction: notificationController.markAsRead,
	onSuccess: () => {
		invalidateNotificationQueries.list();
		invalidateNotificationQueries.unreadCount();
	},
});

const useMarkAllAsRead = makeMutation({
	mutationFunction: notificationController.markAllAsRead,
	onSuccess: () => {
		invalidateNotificationQueries.list();
		invalidateNotificationQueries.unreadCount();
	},
});

const useUpdatePreferences = makeMutation({
	mutationFunction: notificationController.updatePreferences,
	onSuccess: () => {
		invalidateNotificationQueries.preferences();
	},
});

// --- Export ---

export const notificationService = {
	useGetNotifications,
	useGetUnreadCount,
	useGetPreferences,
	useMarkAsRead,
	useMarkAllAsRead,
	useUpdatePreferences,
	keys: notificationServiceKeys,
	invalidate: invalidateNotificationQueries,
};
